import { html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { ElementInternals } from "element-internals-polyfill/dist/element-internals";

import styles from './usos-flow.scss';
import { UsosFlowStep } from "./usos-flow-step";

/**
 * Component representing a process flow or timeline.
 * {@link UsosFlowStep} elements are used
 */
@customElement('usos-flow')
export class UsosFlow extends LitElement {
    static override styles = styles;

    @state()
    private flowSteps: Array<UsosFlowStep> = [];

    @property({'attribute': 'selected-step', reflect: true})
    selectedStep: string = '';

    private internals: ElementInternals;

    constructor() {
        super();
        this.internals = this.attachInternals();
        this.internals.role = 'list';
        this.addEventListener('usos-flow:select', e => this.selectedStep = e.detail.stepNumber);
        this.addEventListener('usos-flow:numberChanged', e => {
            if (this.selectedStep == e.detail.oldNumber && this.selectedStep != '')
                this.selectedStep = e.detail.newNumber;
        });
    }

    override render() {
        return html`<slot @slotchange=${this._onSlotChange}></slot>`;
    }

    private _onSlotChange(e: Event) {
        this.flowSteps = (e.target as HTMLSlotElement).assignedElements().filter(el => el instanceof UsosFlowStep);
        this.flowSteps.forEach((el, idx) => el.isLastStep = idx == this.flowSteps.length - 1);
        const currentlySelected =
            this.flowSteps.find(el => el.stepNumber == this.selectedStep) ||
            this.flowSteps.find(el => el.status == 'active') ||
            this.flowSteps.find(el => el.status == 'future') ||
            (this.flowSteps.length > 0 ? this.flowSteps[this.flowSteps.length - 1] : null);
        this.selectedStep = currentlySelected?.stepNumber ?? this.selectedStep;
    }

    override willUpdate() {
        const selectedElement = this.flowSteps.find(el => el.stepNumber == this.selectedStep);
        if (selectedElement) {
            selectedElement.isOpen = true;
            this.flowSteps.forEach(el => {
                if (el != selectedElement) el.isOpen = false;
            })
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "usos-flow": UsosFlow,
    }
    interface HTMLElementEventMap {
        'usos-flow:select': CustomEvent<{stepNumber: string}>,
        'usos-flow:numberChanged': CustomEvent<{oldNumber: string, newNumber: string}>,
    }
}
